import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Fullscreen Image Viewer
 * 
 * Opens images in a fullscreen modal with:
 * - Zoom in/out
 * - Download
 * - Navigation between multiple images
 * - Rotate
 * - Pinch to zoom (mobile)
 * - Swipe navigation (mobile)
 */
const ImageViewer = ({ 
  isOpen, 
  onClose, 
  images = [], // Array of image URLs or {url, filename} objects
  initialIndex = 0,
  title = "Image Viewer"
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);

  // Normalize images to always have url and filename
  const normalizedImages = images.map((img, idx) => {
    if (typeof img === 'string') {
      return { url: img, filename: `image-${idx + 1}` };
    }
    return { url: img.url, filename: img.filename || `image-${idx + 1}` };
  });

  const currentImage = normalizedImages[currentIndex];
  const hasMultipleImages = normalizedImages.length > 1;

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setZoom(1);
      setRotation(0);
    }
  }, [isOpen, initialIndex]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          if (hasMultipleImages) goToPrevious();
          break;
        case 'ArrowRight':
          if (hasMultipleImages) goToNext();
          break;
        case '+':
        case '=':
          handleZoomIn();
          break;
        case '-':
          handleZoomOut();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, hasMultipleImages]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? normalizedImages.length - 1 : prev - 1));
    setZoom(1);
    setRotation(0);
  }, [normalizedImages.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === normalizedImages.length - 1 ? 0 : prev + 1));
    setZoom(1);
    setRotation(0);
  }, [normalizedImages.length]);

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.5, 4));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.5, 0.5));
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleDownload = async () => {
    if (!currentImage) return;
    
    setIsDownloading(true);
    try {
      const response = await fetch(currentImage.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get file extension from URL or default to jpg
      const urlParts = currentImage.url.split('.');
      const extension = urlParts.length > 1 ? urlParts[urlParts.length - 1].split('?')[0] : 'jpg';
      
      link.download = `${currentImage.filename}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
      // Fallback: open in new tab
      window.open(currentImage.url, '_blank');
    } finally {
      setIsDownloading(false);
    }
  };

  if (!isOpen || !currentImage) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/50 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <h3 className="text-white font-medium text-lg truncate max-w-[200px] sm:max-w-none">
            {title}
          </h3>
          {hasMultipleImages && (
            <span className="text-white/70 text-sm">
              {currentIndex + 1} / {normalizedImages.length}
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {/* Zoom controls */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomOut}
            className="text-white hover:bg-white/20 h-10 w-10 p-0"
            title="Zoom out (-)"
          >
            <ZoomOut className="w-5 h-5" />
          </Button>
          <span className="text-white/70 text-sm w-12 text-center">
            {Math.round(zoom * 100)}%
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomIn}
            className="text-white hover:bg-white/20 h-10 w-10 p-0"
            title="Zoom in (+)"
          >
            <ZoomIn className="w-5 h-5" />
          </Button>
          
          {/* Rotate */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRotate}
            className="text-white hover:bg-white/20 h-10 w-10 p-0"
            title="Rotate"
          >
            <RotateCw className="w-5 h-5" />
          </Button>
          
          {/* Download */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDownload}
            disabled={isDownloading}
            className="text-white hover:bg-white/20 h-10 px-3 gap-2"
            title="Download"
          >
            <Download className="w-5 h-5" />
            <span className="hidden sm:inline">Download</span>
          </Button>
          
          {/* Close */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-white hover:bg-white/20 h-10 w-10 p-0 ml-2"
            title="Close (Esc)"
          >
            <X className="w-6 h-6" />
          </Button>
        </div>
      </div>

      {/* Image container */}
      <div 
        className="flex-1 flex items-center justify-center overflow-hidden relative"
        onClick={(e) => {
          // Close if clicking on background (not image)
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        {/* Previous button */}
        {hasMultipleImages && (
          <Button
            variant="ghost"
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 h-12 w-12 p-0 rounded-full z-10"
            title="Previous (←)"
          >
            <ChevronLeft className="w-8 h-8" />
          </Button>
        )}

        {/* Image */}
        <div 
          className="max-w-full max-h-full flex items-center justify-center p-4 overflow-auto"
          style={{ 
            transform: `scale(${zoom}) rotate(${rotation}deg)`,
            transition: 'transform 0.2s ease-out'
          }}
        >
          <img
            src={currentImage.url}
            alt={currentImage.filename}
            className="max-w-full max-h-[calc(100vh-160px)] object-contain select-none"
            draggable={false}
          />
        </div>

        {/* Next button */}
        {hasMultipleImages && (
          <Button
            variant="ghost"
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 h-12 w-12 p-0 rounded-full z-10"
            title="Next (→)"
          >
            <ChevronRight className="w-8 h-8" />
          </Button>
        )}
      </div>

      {/* Footer with filename */}
      <div className="p-4 bg-black/50 backdrop-blur-sm text-center">
        <p className="text-white/80 text-sm truncate">
          {currentImage.filename}
        </p>
        <p className="text-white/50 text-xs mt-1">
          Use arrow keys to navigate • +/- to zoom • Esc to close
        </p>
      </div>

      {/* Thumbnail strip for multiple images */}
      {hasMultipleImages && normalizedImages.length <= 10 && (
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2 p-2 bg-black/50 backdrop-blur-sm rounded-lg">
          {normalizedImages.map((img, idx) => (
            <button
              key={idx}
              onClick={() => {
                setCurrentIndex(idx);
                setZoom(1);
                setRotation(0);
              }}
              className={`w-12 h-12 rounded overflow-hidden border transition-all ${
                idx === currentIndex 
                  ? 'border-white scale-110' 
                  : 'border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              <img
                src={img.url}
                alt={`Thumbnail ${idx + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageViewer;
